import {
  AfterContentInit,
  Component,
  ComponentRef,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  Type,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';

import { ComponentHostDirective } from '@core/directives/component-host.directive';
import { CB2_PREVIEW_BLOC_KEY } from '@core/services/cb2-preview/cb2-preview-bloc-key.token';
import { camelCase } from 'lodash';
import { deepMergeConfigLayers, legacyPropsToBlockConfig } from '@front-platform/content-builder';
import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from './component-factory-config-input.registry';

@Component({
  selector: 'app-component-factory',
  templateUrl: './component-factory.component.html',
  styleUrls: ['./component-factory.component.scss'],
  standalone: true,
  imports: [forwardRef(() => ComponentHostDirective)],
})
export class ComponentFactoryComponent implements AfterContentInit, OnChanges, OnDestroy {
  @Input() data?: object;
  @Input() componentClass?: Type<unknown>;

  @Output() isVisibleChanged = new EventEmitter();

  @ViewChild(ComponentHostDirective, { static: true })
  componentHost!: ComponentHostDirective;

  private readonly destroyed$ = new Subject<boolean>();
  private readonly injector = inject(Injector);
  private componentRef: ComponentRef<unknown> | null = null;
  private componentClassRef: Type<unknown> | null = null;

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.componentRef = null;
  }

  ngAfterContentInit(): void {
    this.syncComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['componentClass'] || changes['data']) {
      this.syncComponent();
    }
  }

  private syncComponent(): void {
    if (!this.componentClass || !this.componentHost) {
      return;
    }

    const isNewInstance = !this.componentRef || this.componentClassRef !== this.componentClass;

    if (isNewInstance) {
      this.componentHost.viewContainerRef.clear();
      this.componentRef = this.componentHost.viewContainerRef.createComponent(this.componentClass, {
        injector: this.createBlockInjector(),
      });
      this.componentClassRef = this.componentClass;
    }

    if (!this.componentRef) {
      return;
    }

    // Apply inputs before visibility: HighlightCard (and similar) read
    // display_opened_shop from config in componentFactoryIsBlockVisible$ via of(...).
    // Wiring first left config empty → undefined → wrong isVisible (empty or duplicated grids).
    applyComponentInputs(this.componentRef, this.data);

    if (isNewInstance) {
      this.wireVisibilitySubscription(this.componentRef);
    } else {
      this.emitVisibilityOnce(this.componentRef);
    }
  }

  private wireVisibilitySubscription(componentRef: ComponentRef<unknown>): void {
    if (!('componentFactoryIsBlockVisible$' in Object(componentRef.instance))) {
      return;
    }

    Object(componentRef.instance)
      .componentFactoryIsBlockVisible$()
      .pipe(
        takeUntil(this.destroyed$),
        tap((isVisible: boolean) => this.isVisibleChanged.emit(isVisible)),
      )
      .subscribe();
  }

  /** Re-evaluate visibility after data updates on an already-created instance. */
  private emitVisibilityOnce(componentRef: ComponentRef<unknown>): void {
    if (!('componentFactoryIsBlockVisible$' in Object(componentRef.instance))) {
      return;
    }

    Object(componentRef.instance)
      .componentFactoryIsBlockVisible$()
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        tap((isVisible: boolean) => this.isVisibleChanged.emit(isVisible)),
      )
      .subscribe();
  }

  private createBlockInjector(): Injector {
    const blocKey = this.readBlocKeyFromData();
    if (!blocKey) {
      return this.injector;
    }

    return Injector.create({
      providers: [{ provide: CB2_PREVIEW_BLOC_KEY, useValue: blocKey }],
      parent: this.injector,
    });
  }

  private readBlocKeyFromData(): string | null {
    return readBlocKeyFromData(this.data);
  }
}

type ComponentInputRef = Pick<ComponentRef<unknown>, 'componentType' | 'setInput'>;

export function applyComponentInputs(
  componentRef: ComponentInputRef,
  data: object | undefined,
): void {
  if (!data) {
    return;
  }

  const inputs =
    (componentRef.componentType as { ɵcmp?: { inputs?: Record<string, unknown> } }).ɵcmp?.inputs ??
    {};

  const configInputDefinition = COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.get(
    componentRef.componentType,
  );
  if (configInputDefinition) {
    if (!inputs['config']) {
      throw new Error(
        `[ComponentFactory] ${componentRef.componentType.name} is registered for config input binding but does not declare a config input.`,
      );
    }

    const config = buildComponentConfig(data, configInputDefinition.keyAliases);
    componentRef.setInput('config', config);
    return;
  }

  Object.entries(data).forEach(([key, value]) => {
    if (inputs[key]) {
      componentRef.setInput(key, value);
    }

    const camelKey = camelCase(key);
    if (inputs[camelKey]) {
      componentRef.setInput(camelKey, value);
    }
  });
}

export function buildComponentConfig(
  data: object,
  keyAliases: Readonly<Record<string, string>> = {},
): Record<string, unknown> {
  const { config, ...legacyProps } = data as Record<string, unknown>;
  const convertedProps = Object.fromEntries(
    Object.entries(legacyPropsToBlockConfig<Record<string, unknown>>(legacyProps)).map(
      ([key, value]) => [keyAliases[key] ?? key, value],
    ),
  );

  if (!isRecord(config)) {
    return convertedProps;
  }

  return deepMergeConfigLayers(convertedProps, config);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function readBlocKeyFromData(data: object | undefined): string | null {
  const blockId = (data as Record<string, unknown> | undefined)?.['blockId'];
  return typeof blockId === 'string' && blockId.length > 0 ? blockId : null;
}
